/**
 * infinite-carousel — Generic, config-driven clone-loop carousel base.
 *
 * Generalises the clone-loop pattern proven in <values-carousel>
 * (assets/section-values-blocks.js) and <mission-carousel>
 * (assets/section-mission-statement.js) into a reusable host element.
 *
 * Markup contract:
 *
 *   <infinite-carousel data-loop="auto|always|never" data-initial-index="0">
 *     <button data-direction="prev">…</button>
 *     <button data-direction="next">…</button>
 *     <ul data-track class="slider slider--mobile slider--tablet-up">
 *       <li>card 1</li>
 *       …
 *     </ul>
 *   </infinite-carousel>
 *
 * Track   = the descendant with [data-track]; its direct children are cards.
 * Arrows  = descendants with [data-direction="prev"] / [data-direction="next"].
 *
 * Track CSS contract: [data-track] MUST lay out its children flex-start.
 * Do NOT apply `justify-content: center` (or `space-around` / `space-evenly`)
 * to the track, and do NOT apply `margin-inline: auto` to the cards. Once
 * clones are injected the row is far wider than the track, and any centring
 * mechanism pushes the leading children — including the originals — into
 * negative `offsetLeft` space that `scrollLeft` cannot reach (it clamps at 0).
 * The visible symptom is a clone-loop wrap that lands at `scrollLeft: 0`
 * instead of the original slide's snap point ("jumps to the beginning").
 * For static fallback centring when the row fits without overflow, scope the
 * rule to `infinite-carousel[data-overflow="false"] [data-track]` so it
 * deactivates the moment clones are built. See rules/javascript.md →
 * **Scroll-Track CSS Contract** for the full pattern.
 *
 * Loop modes (data-loop):
 *   auto    — clones built + arrows shown ONLY when track overflows the
 *             viewport. Host gets data-overflow="true|false" and re-evaluates
 *             on the 750px / 990px breakpoint changes. Default if attr is
 *             missing.
 *   always  — clones built unconditionally; arrows always active. No
 *             data-overflow attribute set.
 *   never   — no clones built; arrows behave like Dawn's <slider-component>
 *             (disabled at the start / end of the track). No data-overflow
 *             attribute set.
 *
 * Resize policy: matchMedia listeners at (min-width: 750px) and
 * (min-width: 990px) only — no ResizeObserver. Listeners are removed in
 * disconnectedCallback so the element is hot-swap safe.
 *
 * Reduced motion: _goTo() falls back to behavior:'auto' whenever
 * prefers-reduced-motion:reduce is set, regardless of the smooth arg.
 *
 * Events: dispatches a `slideChanged` CustomEvent each time the active card
 * changes, with detail { currentPage:<1-indexed-int>, currentElement:<HTMLElement> }
 * — matching Dawn's <slider-component> contract so downstream listeners
 * (e.g. pdp-gallery-dots.js) work without changes.
 *
 * Subclass-friendly hooks (empty in base, overridable):
 *   _onAfterScroll()        — invoked on rAF-throttled scroll
 *   _resolveInitialIndex()  — returns active index to land on; base reads
 *                             data-initial-index (defaults to 0)
 */
/**
 * Anything focusable inside an aria-hidden clone is an aria-hidden-focus
 * violation (WCAG 4.1.2). tabindex="-1" removes it from the tab order while
 * leaving it CLICKABLE — clones are visible cards and must stay
 * pointer-interactive, so `inert` is deliberately NOT used.
 */
const CLONE_FOCUSABLE =
  'a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

class InfiniteCarousel extends HTMLElement {
  connectedCallback() {
    this._track = this.querySelector('[data-track]');
    if (!this._track) return;

    this._origCards = Array.from(this._track.children);
    this._count = this._origCards.length;
    if (this._count === 0) return;

    this._mode = this.getAttribute('data-loop') || 'auto';
    this._prevButton = this.querySelector('[data-direction="prev"]');
    this._nextButton = this.querySelector('[data-direction="next"]');

    this._rafId = null;
    this._scrollEndTimer = null;
    this._currentPage = 0;
    this._reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    // matchMedia listeners — only used in auto mode. Bound once so add /
    // remove receive the same reference.
    this._mq750 = window.matchMedia('(min-width: 750px)');
    this._mq990 = window.matchMedia('(min-width: 990px)');
    this._onBreakpoint = () => this._handleBreakpointChange();

    if (this._mode === 'auto') {
      this._mq750.addEventListener('change', this._onBreakpoint);
      this._mq990.addEventListener('change', this._onBreakpoint);
    }

    this._bindArrows();
    this._bindScroll();

    requestAnimationFrame(() => {
      this._evaluateAndBuild();
      this._goTo(this._resolveInitialIndex(), false);
      this._updateButtons();
      this.setAttribute('data-initialized', '');
    });
  }

  disconnectedCallback() {
    this._unwatchCloneFocus();
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._scrollEndTimer) {
      clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = null;
    }
    if (this._mode === 'auto') {
      this._mq750?.removeEventListener('change', this._onBreakpoint);
      this._mq990?.removeEventListener('change', this._onBreakpoint);
    }
  }

  /* ── Subclass hooks (empty in base) ───────────────────────────────────── */

  _onAfterScroll() {}

  _resolveInitialIndex() {
    const raw = parseInt(this.getAttribute('data-initial-index'), 10);
    const base = Number.isFinite(raw) && raw >= 0 ? raw : 0;
    // When clones are built, the centre copy of the original set starts at
    // offset this._count.
    return this._hasClones ? this._count + base : base;
  }

  /* ── Mode evaluation + clone management ───────────────────────────────── */

  _evaluateAndBuild() {
    this._hasClones = false;

    if (this._mode === 'always') {
      this._buildClones();
    } else if (this._mode === 'auto') {
      const overflows = this._trackOverflows();
      this.setAttribute('data-overflow', overflows ? 'true' : 'false');
      if (overflows) this._buildClones();
    }
    // 'never' — no clones, no data-overflow attribute.

    this._allCards = Array.from(this._track.children);
  }

  _trackOverflows() {
    return this._track.scrollWidth > this._track.clientWidth + 1;
  }

  _handleBreakpointChange() {
    if (this._mode !== 'auto') return;

    const wasOverflow = this.getAttribute('data-overflow') === 'true';
    const nowOverflow = this._trackOverflows();

    if (wasOverflow === nowOverflow) {
      // Still need to refresh attr to be safe (e.g. first paint vs media
      // change race).
      this.setAttribute('data-overflow', nowOverflow ? 'true' : 'false');
      return;
    }

    if (nowOverflow && !wasOverflow) {
      this.setAttribute('data-overflow', 'true');
      this._buildClones();
      this._allCards = Array.from(this._track.children);
      requestAnimationFrame(() => {
        this._goTo(this._resolveInitialIndex(), false);
        this._updateButtons();
      });
    } else if (!nowOverflow && wasOverflow) {
      this.setAttribute('data-overflow', 'false');
      this._teardownClones();
      this._allCards = Array.from(this._track.children);
      this._track.scrollLeft = 0;
      this._updateButtons();
    }
  }

  _buildClones() {
    const makeClones = () =>
      this._origCards.map((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        return clone;
      });

    // Prepend in reverse so they arrive in correct left-to-right visual order.
    makeClones()
      .reverse()
      .forEach((c) => this._track.prepend(c));

    makeClones().forEach((c) => this._track.append(c));

    this._hasClones = true;
    this._neutraliseCloneFocus();
    this._watchCloneFocus();
  }

  /** Take every focusable node inside every clone out of the tab order. */
  _neutraliseCloneFocus() {
    const mute = (el) => {
      // Guard: only write when the value actually changes. setAttribute queues a
      // MutationObserver record even for an identical value, so an unguarded
      // write here would feed _watchCloneFocus() forever.
      if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
    };
    this._track.querySelectorAll(':scope > [data-clone="true"]').forEach((clone) => {
      if (clone.matches(CLONE_FOCUSABLE)) mute(clone);
      clone.querySelectorAll(CLONE_FOCUSABLE).forEach(mute);
    });
  }

  /**
   * Third-party widgets hydrate INSIDE clones that are already built and
   * re-introduce the violation: Reviews.io's `.ruk_rating_snippet` sets
   * tabindex="0" on a rating div after _buildClones() has run, which is why a
   * build-time stamp alone left 12 of 24 clones still focusable.
   */
  _watchCloneFocus() {
    if (this._cloneFocusObserver) return;
    this._cloneFocusObserver = new MutationObserver(() => {
      if (this._hasClones) this._neutraliseCloneFocus();
    });
    this._cloneFocusObserver.observe(this._track, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });
  }

  _unwatchCloneFocus() {
    this._cloneFocusObserver?.disconnect();
    this._cloneFocusObserver = null;
  }

  _teardownClones() {
    this._unwatchCloneFocus();
    this._track
      .querySelectorAll(':scope > [data-clone="true"]')
      .forEach((el) => el.remove());
    this._hasClones = false;
  }

  /* ── Scrolling ────────────────────────────────────────────────────────── */

  _goTo(idx, smooth) {
    const card = this._allCards?.[idx];
    if (!card) return;
    // Match the browser's scroll-snap target (scroll-snap-align: start +
    // scroll-padding-left from Dawn's .slider--mobile / .slider--tablet-up)
    // so neither smooth nor instant landings trigger a post-arrival snap-pull.
    const padLeft = parseFloat(getComputedStyle(this._track).scrollPaddingLeft) || 0;
    const left = Math.max(0, card.offsetLeft - padLeft);
    const useSmooth = smooth && !this._reducedMotionMq.matches;
    if (useSmooth) {
      this._track.scrollTo({ left, behavior: 'smooth' });
    } else {
      // Bypass any inherited CSS `scroll-behavior: smooth` (Dawn's
      // .slider--mobile / .slider--tablet-up both set it) so silent jumps
      // — clone-loop wrap-around and breakpoint rebuild — are truly instant.
      const prev = this._track.style.scrollBehavior;
      this._track.style.scrollBehavior = 'auto';
      this._track.scrollLeft = left;
      requestAnimationFrame(() => {
        this._track.style.scrollBehavior = prev;
      });
    }
  }

  _nearestIndex() {
    if (!this._allCards?.length) return 0;
    // Anchor at the scroll-snap target (scroll-snap-align: start +
    // scroll-padding-left) so "current card" agrees with where _goTo lands
    // and the browser snaps. Card-center vs view-center drifts off-by-one in
    // 2-up layouts where the active card sits left of viewport center.
    const padLeft = parseFloat(getComputedStyle(this._track).scrollPaddingLeft) || 0;
    const target = this._track.scrollLeft + padLeft;
    let nearest = 0;
    let minDist = Infinity;
    this._allCards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft - target);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  }

  _handleLoop() {
    if (!this._hasClones) {
      // Non-looping mode — just update buttons + emit event.
      this._activeIdx = this._nearestIndex();
      this._emitSlideChanged(this._activeIdx);
      this._updateButtons();
      return;
    }

    const idx = this._nearestIndex();
    let target = null;

    if (idx < this._count) {
      target = idx + this._count;
    } else if (idx >= this._count * 2) {
      target = idx - this._count;
    }

    if (target !== null) {
      this._activeIdx = target;
      this._track.style.scrollSnapType = 'none';
      this._goTo(target, false);
      requestAnimationFrame(() => {
        this._track.style.scrollSnapType = '';
      });
    } else {
      this._activeIdx = idx;
    }

    this._emitSlideChanged(this._activeIdx);
  }

  _shift(dir) {
    const nearest = this._nearestIndex();
    let target = nearest + dir;

    if (!this._hasClones) {
      // never-mode / no-overflow auto: clamp at edges.
      if (target < 0 || target >= this._allCards.length) return;
      this._activeIdx = target;
      this._goTo(target, true);
      return;
    }

    // Clone-loop mode: if scrollend hasn't yet re-centred us into the
    // original set and the step would fall out of bounds, silently re-centre
    // first so the user's click always advances by exactly one card.
    if (target < 0 || target >= this._allCards.length) {
      const recentered =
        ((nearest - this._count) % this._count + this._count) % this._count +
        this._count;
      this._goTo(recentered, false);
      target = recentered + dir;
    }

    this._activeIdx = target;
    this._goTo(target, true);
  }

  /* ── Buttons + events ─────────────────────────────────────────────────── */

  _bindArrows() {
    this._prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this._shift(-1);
    });
    this._nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this._shift(1);
    });
  }

  _bindScroll() {
    this._track.addEventListener(
      'scroll',
      () => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._onAfterScroll();
          this._updateCurrentPage();
          this._updateButtons();
        });
      },
      { passive: true }
    );

    const onSettle = () => this._handleLoop();

    if ('onscrollend' in window) {
      this._track.addEventListener('scrollend', onSettle, { passive: true });
    } else {
      this._track.addEventListener(
        'scroll',
        () => {
          clearTimeout(this._scrollEndTimer);
          this._scrollEndTimer = setTimeout(onSettle, 200);
        },
        { passive: true }
      );
    }
  }

  _updateCurrentPage() {
    this._emitSlideChanged(this._nearestIndex());
  }

  // Single emission path. Dedups against the previous 1-indexed currentPage
  // so neither the rAF-throttled scroll handler nor _handleLoop's scrollend
  // pass can fire duplicate events for the same active card.
  _emitSlideChanged(allCardsIdx) {
    const card = this._allCards?.[allCardsIdx];
    if (!card) return;
    let originalIdx = allCardsIdx;
    if (this._hasClones) {
      originalIdx = ((allCardsIdx - this._count) % this._count + this._count) % this._count;
    }
    const newPage = originalIdx + 1;
    if (newPage === this._currentPage) return;
    this._currentPage = newPage;
    this.dispatchEvent(
      new CustomEvent('slideChanged', {
        detail: {
          currentPage: newPage,
          currentElement: card,
        },
      })
    );
  }

  _updateButtons() {
    if (!this._prevButton || !this._nextButton) return;

    // Looping modes never disable the arrows.
    if (this._hasClones) {
      this._prevButton.removeAttribute('disabled');
      this._nextButton.removeAttribute('disabled');
      return;
    }

    // No clones (never mode, or auto with no overflow) — disable at edges
    // like Dawn's <slider-component>.
    const atStart = this._track.scrollLeft <= 0;
    const atEnd =
      this._track.scrollLeft + this._track.clientWidth >=
      this._track.scrollWidth - 1;

    if (atStart) {
      this._prevButton.setAttribute('disabled', 'disabled');
    } else {
      this._prevButton.removeAttribute('disabled');
    }
    if (atEnd) {
      this._nextButton.setAttribute('disabled', 'disabled');
    } else {
      this._nextButton.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('infinite-carousel')) {
  customElements.define('infinite-carousel', InfiniteCarousel);
}

// Expose the constructor so section-specific subclasses loaded as separate
// (classic, deferred) scripts can `extends` it. Top-level `class` declarations
// do NOT attach to `window`, so a bare cross-script reference is fragile; this
// makes the subclassing contract explicit. See assets/section-shop-by-age-page-
// cta.js for a consumer.
window.InfiniteCarousel = InfiniteCarousel;
